import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Inbox, Link2, PlusCircle, ChevronDown, ChevronRight, Info, Boxes,
  Copy, Puzzle, Package, Ban, CircleSlash, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { conceptName, shortTitle } from "@/lib/breakoutLabels";

const InfoDot = ({ text }) => (
  <span title={text} className="inline-flex cursor-help text-zinc-500 hover:text-zinc-300 transition-colors"><Info size={13} /></span>
);

const MORE_ACTIONS = [
  { key: "MARK_VARIANT_OF", label: "Variant of another product", icon: Copy, needsConcept: true },
  { key: "MARK_ACCESSORY_OF", label: "Accessory of another product", icon: Puzzle, needsConcept: true },
  { key: "MARK_BUNDLE", label: "Bundle", icon: Package, needsConcept: true },
  { key: "EXCLUDE", label: "Exclude from radar", icon: Ban, needsConcept: false, danger: true },
  { key: "KEEP_UNRESOLVED", label: "Keep unresolved for now", icon: CircleSlash, needsConcept: false },
];

function ReviewCard({ it, onDone }) {
  const [name, setName] = useState("");
  const [showTitle, setShowTitle] = useState(false);
  const [more, setMore] = useState(false);
  const [tech, setTech] = useState(false);
  const matches = (it.proposed_matches || []).map((m) => ({ id: m, name: conceptName(m) }));

  const submit = async (action, concept, needsConcept) => {
    if (needsConcept && !concept) { toast.error("Enter the correct product name first"); return; }
    try {
      await api.post("/amazon/review-action", { asin: it.asin, action, concept });
      toast.success("Saved · thanks for confirming");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`review-item-${it.asin}`} className="bg-[#18181B] border border-[#27272A] rounded-xl p-5">
      <div className="flex gap-4">
        <div className="w-20 h-20 shrink-0 rounded-lg border border-[#27272A] bg-[#0e0e10] flex items-center justify-center overflow-hidden">
          {it.image_url ? <img src={it.image_url} alt="" className="w-full h-full object-contain" /> : <Boxes size={20} className="text-zinc-700" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">Amazon listing</div>
          <div className={`text-sm text-zinc-200 leading-snug ${showTitle ? "" : "line-clamp-2"}`}>{it.title}</div>
          {(it.title || "").length > 90 && (
            <button onClick={() => setShowTitle((s) => !s)} className="text-xs text-zinc-500 hover:text-zinc-300 mt-0.5">
              {showTitle ? "Show less" : "View full title"}
            </button>
          )}
          <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-zinc-500">
            <span>Amazon rank <span className="text-zinc-300 font-medium">#{it.latest_rank ?? "—"}</span></span>
            <span>first seen {it.first_seen}</span>
            <span>seen {it.observations} day{it.observations === 1 ? "" : "s"}</span>
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-lg border border-[#232326] bg-[#0e0e10] p-4">
        <div className="text-sm font-medium text-zinc-200">What product is this?</div>
        <div className="flex items-center flex-wrap gap-2 mt-1.5 text-xs">
          <span className="text-zinc-500">Our best guess:</span>
          <span className="text-zinc-300 italic">"{conceptName(it.proposed_concept) || "unknown"}"</span>
          <span title="Our automatic product resolver was not confident enough to classify this listing safely." className="inline-flex items-center gap-1 text-amber-400 border border-amber-400/20 bg-amber-400/10 rounded-full px-2 py-0.5">
            <AlertTriangle size={11} /> Needs human confirmation
          </span>
        </div>

        {matches.length > 0 && (
          <div className="mt-4">
            <div className="text-xs text-zinc-500 mb-1.5">Possible existing match{matches.length > 1 ? "es" : ""}</div>
            <div className="flex flex-col gap-1.5">
              {matches.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-md border border-[#27272A] bg-[#141416] px-3 py-2">
                  <span className="text-sm text-zinc-200 capitalize">{m.name}</span>
                  <button data-testid={`use-match-${it.asin}`} onClick={() => submit("CONFIRM_EXISTING_CONCEPT", m.name, true)}
                    className="text-xs font-medium text-emerald-400 hover:text-emerald-300 border border-emerald-400/20 bg-emerald-400/10 rounded-md px-2.5 py-1 transition-colors">
                    Use this product
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4">
          <label className="text-xs text-zinc-400 flex items-center gap-1.5">Correct product name</label>
          <input
            data-testid={`concept-input-${it.asin}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Example: Meat Thermometer"
            className="mt-1.5 w-full bg-[#09090B] border border-[#27272A] rounded-md px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500/60 outline-none"
          />
          <div className="text-xs text-zinc-500 mt-1">Core product name only — leave out brand, color, size, quantity and marketing words.</div>
        </div>

        <div className="flex items-center gap-2.5 mt-4 flex-wrap">
          <button data-testid={`action-link-${it.asin}`} onClick={() => submit("CONFIRM_EXISTING_CONCEPT", name.trim(), true)}
            className="inline-flex items-center gap-1.5 text-sm font-medium bg-emerald-500 text-[#09090B] hover:bg-emerald-400 rounded-md px-3.5 py-2 transition-colors">
            <Link2 size={14} /> Link to existing product
          </button>
          <button data-testid={`action-create-${it.asin}`} onClick={() => submit("CREATE_NEW_CANONICAL_CONCEPT", name.trim(), true)}
            className="inline-flex items-center gap-1.5 text-sm font-medium border border-emerald-400/30 text-emerald-300 hover:bg-emerald-400/10 rounded-md px-3.5 py-2 transition-colors">
            <PlusCircle size={14} /> Create new product
          </button>
        </div>

        <div className="mt-3">
          <button data-testid={`more-toggle-${it.asin}`} onClick={() => setMore((m) => !m)} className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1.5">
            {more ? <ChevronDown size={13} /> : <ChevronRight size={13} />} More options
          </button>
          {more && (
            <div className="mt-2 flex flex-col gap-1">
              {MORE_ACTIONS.map((a) => (
                <button key={a.key} data-testid={`action-${a.key}-${it.asin}`} onClick={() => submit(a.key, name.trim(), a.needsConcept)}
                  className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-md text-left transition-colors ${a.danger ? "text-rose-400 hover:bg-rose-400/10" : "text-zinc-300 hover:bg-zinc-800"}`}>
                  <a.icon size={13} /> {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <button data-testid={`tech-toggle-${it.asin}`} onClick={() => setTech((t) => !t)} className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1.5 mt-3">
        {tech ? <ChevronDown size={13} /> : <ChevronRight size={13} />} View technical details
      </button>
      {tech && (
        <div className="mt-2 rounded-lg border border-[#232326] bg-[#0e0e10] p-3 text-[11px] font-mono text-zinc-400 space-y-1">
          <div>asin: {it.asin}</div>
          <div>resolver: {it.extraction_reason}</div>
          <div>proposed_concept: {it.proposed_concept} · confidence: {it.proposed_confidence}</div>
          <div>category: {it.category}</div>
        </div>
      )}
    </div>
  );
}

export default function NRReview() {
  const [d, setD] = useState(null);
  const load = () => api.get("/amazon/review-queue").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="p-8 text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="p-8 max-w-[900px] mx-auto" data-testid="nr-review-page">
      <div className="flex items-center gap-2.5 mb-1.5">
        <Inbox className="text-emerald-400" size={22} />
        <h1 className="font-head text-2xl font-bold tracking-tight text-zinc-50">Products needing review</h1>
      </div>
      <p className="text-sm text-zinc-400 max-w-2xl">
        Breakout Radar isn't sure what these products are. Help confirm the correct product name so we can track their movement accurately.
      </p>
      <p className="text-xs text-zinc-500 mt-1.5 flex items-center gap-1.5">
        <Info size={12} /> You're identifying the product — not deciding whether it's a good opportunity.
      </p>

      <div className="text-sm text-zinc-400 mt-6 mb-4" data-testid="review-count">
        <span className="text-zinc-100 font-semibold">{d.total}</span> product{d.total === 1 ? "" : "s"} need review
      </div>

      {d.items.length === 0 ? (
        <div className="border border-dashed border-[#27272A] rounded-xl p-8 text-center text-sm text-zinc-500" data-testid="nr-review-empty">
          All caught up — every tracked listing is either automatically identified or human-confirmed.
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {d.items.map((it) => <ReviewCard key={it.asin} it={it} onDone={load} />)}
        </div>
      )}
    </div>
  );
}
